import { InputDescription, InputName } from "@components-common/forms";
import { useField } from "formik";
import { FC } from "react";
import { WindowMeasureInput } from "./WindowMeasureInput";
import { Col } from "taltech-styleguide";
import { WindowStyleSelector } from "./WindowStyleSelector";

/**
 * Functional Component: WindowMeasuresDetails
 *
 * This component renders input fields for window measurements and style.
 * It includes selectors for window style and inputs for sill height, width, and height with conditional disabling based on the field values.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the window style selector and measurement inputs.
 */
const WindowMeasuresDetails = () => {
  const [fieldStyle] = useField("style");
  const [fieldHeight] = useField("height");

  return (
    <>
      <WindowStyleSelector elementClass="required" />
      <WindowMeasureInput
        id="width"
        label="Width"
        disabled={fieldStyle.value === "WALL_TO_WALL"}
      />
      <WindowMeasureInput
        id="height"
        label="Height"
        disabled={fieldHeight.value.maximum === true}
        showMaximum={true}
      />
      <WindowMeasureInput
        id="sillHeight"
        label="Sill height"
        disabled={
          fieldHeight.value.maximum === true ||
          fieldStyle.value === "WALL_TO_WALL"
        }
      />
    </>
  );
};

/**
 * Functional Component: WindowDataForm
 *
 * This component renders a form for window data, including inputs for the window name, measurements, and description.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the form inputs for window data.
 */
export const WindowDataForm: FC = () => {
  return (
    <div className="mt-3">
      <Col xs="4">
        <InputName elementClass="required" showHelp={false} />
      </Col>
      <Col xs="7">
        <WindowMeasuresDetails />
      </Col>
      <Col xs="7" className="mt-2">
        <InputDescription showHelp={false} />
      </Col>
    </div>
  );
};
